import React, { useState, useEffect, useCallback } from 'react';
import collegeConfig from '../../config/collegeConfig';
import DrpHistoryModal from '../shared/DrpHistoryModal';
import {
  FaHospital, FaUserMd, FaCalendarAlt,
  FaAward, FaSearch,
  FaSpinner, FaEye, FaHistory, FaUpload
} from 'react-icons/fa';

const API = collegeConfig.api.baseURL;

const formatDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const isCompleted = (toDate) => {
  if (!toDate) return false;
  const t = new Date(toDate); t.setHours(0, 0, 0, 0);
  const n = new Date(); n.setHours(0, 0, 0, 0);
  return t < n;
};

const logCount = (entry) =>
  (entry?.attendance?.length || 0) + (entry?.diary?.length || 0) +
  (entry?.operations?.length || 0) + (entry?.publicHealth?.length || 0);

const isCertified = (entry) => !!(entry?.certificate?.issued || entry?.uploadedCertificate?.fileName);

const ClassicDRPMonitor = () => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [search, setSearch] = useState('');

  const token = localStorage.getItem('token');

  const loadStudents = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/classic-drp/hod/students`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json();
      if (d.success) setStudents(d.students);
    } catch { /* silent */ }
    setLoading(false);
  }, [token]);

  useEffect(() => { loadStudents(); }, [loadStudents]);

  const filtered = students.filter(item => {
    if (!search) return true;
    const s = item.student;
    const name = `${s?.profile?.firstName || ''} ${s?.profile?.lastName || ''}`.toLowerCase();
    return name.includes(search.toLowerCase()) || (s?.profile?.registrationNumber || '').toLowerCase().includes(search.toLowerCase());
  });

  if (loading) return (
    <div style={styles.centered}>
      <FaSpinner style={{ animation: 'spin 1s linear infinite', fontSize: 24, color: '#b11217' }} />
      <style>{`@keyframes spin { from{transform:rotate(0deg);}to{transform:rotate(360deg);}}`}</style>
    </div>
  );

  // ── Student List ──────────────────────────────────────────────────────────────
  return (
    <div style={{ marginTop: 24 }}>
      {selectedStudentId && (
        <DrpHistoryModal studentId={selectedStudentId} onClose={() => setSelectedStudentId(null)} />
      )}

      <div style={styles.header}>
        <FaHospital style={{ color: '#E67E22', fontSize: 20 }} />
        <div>
          <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#1a1a2e' }}>Classic DRP Activity Monitor</h2>
          <p style={{ margin: '2px 0 0', fontSize: 13, color: '#666' }}>Read-only view of student DRP activities. Most students complete a posting via certificate only — empty activity logs are normal, not missing data.</p>
        </div>
      </div>

      {/* Stats */}
      <div style={styles.statsRow}>
        <div style={styles.statCard}>
          <span style={styles.statNum}>{students.length}</span>
          <span style={styles.statLabel}>Total Students</span>
        </div>
        <div style={styles.statCard}>
          <span style={{ ...styles.statNum, color: '#059669' }}>{students.filter(s => isCertified(s.entry)).length}</span>
          <span style={styles.statLabel}>Certified</span>
        </div>
        <div style={styles.statCard}>
          <span style={{ ...styles.statNum, color: '#0891b2' }}>{students.filter(s => isCertified(s.entry) && logCount(s.entry) === 0).length}</span>
          <span style={styles.statLabel}>Posting Only</span>
        </div>
        <div style={styles.statCard}>
          <span style={{ ...styles.statNum, color: '#2563eb' }}>
            {students.filter(s => {
              const v = s.entry?.inchargeVerification || {};
              return v.attendanceVerified && v.diaryVerified && v.operationsVerified && v.publicHealthVerified && !isCertified(s.entry);
            }).length}
          </span>
          <span style={styles.statLabel}>Verified</span>
        </div>
        <div style={styles.statCard}>
          <span style={{ ...styles.statNum, color: '#7c3aed' }}>{students.filter(s => (s.entry?.drpHistory?.length || 0) > 0).length}</span>
          <span style={styles.statLabel}>Multi-DRP</span>
        </div>
      </div>

      {/* Search */}
      <div style={styles.searchBox}>
        <FaSearch style={{ color: '#999', position: 'absolute', left: 12, top: 11 }} />
        <input type="text" placeholder="Search by student name or registration number..." value={search} onChange={e => setSearch(e.target.value)} style={styles.searchInput} />
      </div>

      {filtered.length === 0 ? (
        <div style={styles.emptyState}>
          <FaUserMd style={{ fontSize: 40, color: '#ccc', marginBottom: 12 }} />
          <p style={{ color: '#999', fontSize: 14 }}>No Classic DRP data found for students in your course.</p>
          <p style={{ color: '#bbb', fontSize: 12 }}>Students will appear here once they start their Classic DRP activities.</p>
        </div>
      ) : (
        filtered.map(({ student: s, entry }) => {
          const v = entry?.inchargeVerification || {};
          const allVerified = v.attendanceVerified && v.diaryVerified && v.operationsVerified && v.publicHealthVerified;
          const certified = isCertified(entry);
          const entryLogCount = logCount(entry);
          const studentName = `${s?.profile?.firstName || ''} ${s?.profile?.lastName || ''}`.trim();
          const inchargeData = entry?.profile?.inchargeId;
          const inchargeName = inchargeData ? `${inchargeData.profile?.firstName || ''} ${inchargeData.profile?.lastName || ''}`.trim() : null;
          const histCount = entry?.drpHistory?.length || 0;
          const totalDRPs = histCount + (entry?.profile?.hospitalName ? 1 : 0);
          const currentCompleted = isCompleted(entry?.profile?.toDate);

          return (
            <div key={s._id} style={styles.studentCard}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <div style={styles.avatar}>{studentName.charAt(0).toUpperCase()}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                    <div>
                      <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#1a1a2e' }}>{studentName}</h3>
                      <p style={{ margin: '2px 0', fontSize: 12, color: '#666' }}>{s?.assignedCourses?.[0]?.courseName || ''} · {s?.profile?.registrationNumber || ''}</p>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                      {certified ? (
                        <span style={styles.badgeCert}>
                          {entry?.certificate?.issued ? <FaAward size={10} style={{ marginRight: 4 }} /> : <FaUpload size={10} style={{ marginRight: 4 }} />}
                          {entry?.certificate?.issued ? 'System Cert' : 'Uploaded Cert'}
                        </span>
                      ) : allVerified ? (
                        <span style={styles.badgeGreen}>All Verified</span>
                      ) : entryLogCount > 0 ? (
                        <span style={styles.badgeGray}>In Progress</span>
                      ) : entry ? (
                        <span style={styles.badgeGray}>Posting Started</span>
                      ) : (
                        <span style={styles.badgeGray}>Not Started</span>
                      )}
                      {totalDRPs > 1 && (
                        <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 8, background: '#ede9fe', color: '#7c3aed', fontWeight: 600 }}>
                          <FaHistory size={8} style={{ marginRight: 3 }} />{totalDRPs} DRPs
                        </span>
                      )}
                    </div>
                  </div>
                  {entry?.profile?.hospitalName && (
                    <p style={{ margin: '3px 0', fontSize: 12, color: '#888' }}>
                      <FaHospital size={10} style={{ marginRight: 4 }} />{entry.profile.hospitalName}
                      {inchargeName && <span style={{ marginLeft: 8 }}><FaUserMd size={10} style={{ marginRight: 4 }} />{inchargeName}</span>}
                      {currentCompleted && <span style={{ marginLeft: 8, color: '#059669', fontSize: 11 }}>(Completed)</span>}
                    </p>
                  )}
                  {entry?.profile?.fromDate && (
                    <p style={{ margin: '2px 0', fontSize: 12, color: '#888' }}>
                      <FaCalendarAlt size={10} style={{ marginRight: 4 }} />
                      {formatDate(entry.profile.fromDate)} – {formatDate(entry.profile.toDate)}
                    </p>
                  )}
                  {entry && entryLogCount > 0 && (
                    <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                      {[
                        { key: 'attendanceVerified', label: 'Attendance', count: entry.attendance?.length || 0 },
                        { key: 'diaryVerified', label: 'Diary', count: entry.diary?.length || 0 },
                        { key: 'operationsVerified', label: 'Ops', count: entry.operations?.length || 0 },
                        { key: 'publicHealthVerified', label: 'PH', count: entry.publicHealth?.length || 0 }
                      ].map(item => (
                        <span key={item.key} style={{ fontSize: 11, padding: '2px 7px', borderRadius: 10, background: v[item.key] ? '#d1fae5' : '#f3f4f6', color: v[item.key] ? '#065f46' : '#666' }}>
                          {v[item.key] ? '✓' : '○'} {item.label} ({item.count})
                        </span>
                      ))}
                    </div>
                  )}
                  {entry && entryLogCount === 0 && (
                    <p style={{ margin: '6px 0 0', fontSize: 11, color: '#999' }}>
                      {certified ? 'Certificate submitted — no activity logs recorded' : 'No activity logs recorded yet'}
                    </p>
                  )}
                </div>
              </div>
              {entry && (
                <button onClick={() => setSelectedStudentId(s._id)} style={styles.viewBtn}>
                  <FaEye style={{ marginRight: 6 }} /> View Details{totalDRPs > 1 ? ` (${totalDRPs} DRPs)` : ''}
                </button>
              )}
            </div>
          );
        })
      )}
    </div>
  );
};

const styles = {
  centered: { display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 40, gap: 10 },
  header: { display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 16, padding: '14px 16px', background: '#fff8f0', border: '1px solid #fed7aa', borderRadius: 12 },
  statsRow: { display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' },
  statCard: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '12px 20px', textAlign: 'center', flex: '1 0 80px' },
  statNum: { display: 'block', fontSize: 22, fontWeight: 800, color: '#b11217' },
  statLabel: { fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: '0.4px' },
  searchBox: { position: 'relative', marginBottom: 14 },
  searchInput: { width: '100%', padding: '9px 12px 9px 36px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', outline: 'none' },
  emptyState: { textAlign: 'center', padding: '40px 20px', color: '#999' },
  studentCard: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 14, marginBottom: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' },
  avatar: { width: 40, height: 40, borderRadius: '50%', background: '#b11217', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 700, flexShrink: 0 },
  viewBtn: { display: 'flex', alignItems: 'center', marginTop: 10, padding: '6px 14px', background: 'none', border: '1px solid #b11217', color: '#b11217', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 },
  badgeCert: { fontSize: 11, padding: '3px 8px', borderRadius: 10, background: '#d97706', color: '#fff', fontWeight: 600, display: 'inline-flex', alignItems: 'center' },
  badgeGreen: { fontSize: 11, padding: '3px 8px', borderRadius: 10, background: '#d1fae5', color: '#059669', fontWeight: 600 },
  badgeGray: { fontSize: 11, padding: '3px 8px', borderRadius: 10, background: '#f3f4f6', color: '#666', fontWeight: 500 }
};

export default ClassicDRPMonitor;
